/**
 * Resolve which engine (midi vs media link) the app-level NowPlayingHost should mount.
 * Explicit /playMidi|/playMedia URLs beat a stale committed route; ref-based route
 * still beats lagged React state when the URL does not force an engine.
 */
import { hasFilteredPlaybackVoices } from './abcVoiceViewSettings'

export function resolveHostPlaybackTarget(mediaController, playingTune, tunebook, queue, currentItem, urlPlayback, helpers) {
  const h = helpers || {}
  const isQueueActiveFn = h.isQueueActive || function() { return false }
  const resolvePlaybackForItemFn = h.resolvePlaybackForItem || function() { return null }

  if (!playingTune || !tunebook || !mediaController) return null

  const hasMusic = tunebook.hasNotesOrChords && tunebook.hasNotesOrChords(playingTune)
  const hasLinks = Array.isArray(playingTune.links) && playingTune.links.length > 0

  function resolveActiveMediaLinkNum(fallbackLinkNum) {
    if (mediaController.mediaLinkNumber != null) {
      return mediaController.mediaLinkNumber
    }
    if (fallbackLinkNum != null) {
      const parsed = parseInt(fallbackLinkNum, 10)
      if (!isNaN(parsed)) return parsed
    }
    return 0
  }

  // Deep-link /playMidi must win over a leftover YouTube/media route (restored
  // queue, prior tune, etc.). /playMedia remains the URL that forces linked audio.
  if (urlPlayback && urlPlayback.playState === 'playMidi' && hasMusic) {
    return { type: 'midi' }
  }

  if (urlPlayback && urlPlayback.playState === 'playMedia' && hasLinks) {
    // /playMedia URLs can lag behind an explicit MIDI request (navigate not
    // flushed, or navigation suppressed). Prefer controller MIDI intent so the
    // host does not flip midi↔media and remount Abc mid-kickoff.
    const wantsMidiOverStaleMediaUrl = hasMusic && (
      mediaController.requestedPlayState === 'playMidi'
      || (
        mediaController.requestedPlayState !== 'playMedia'
        && mediaController.playbackRouteMode === 'midi'
        && (
          !!mediaController.isLoading
          || !!(mediaController.pendingMidiPlayRef && mediaController.pendingMidiPlayRef.current)
          || !!(mediaController.hasActivePlaybackIntent
            && mediaController.hasActivePlaybackIntent())
          || !!(mediaController.isMidiPlaybackRoute
            && mediaController.isMidiPlaybackRoute())
        )
      )
    )
    if (!wantsMidiOverStaleMediaUrl) {
      const linkNum = parseInt(urlPlayback.mediaLinkNumber, 10) || 0
      return { type: 'media', linkNum: linkNum }
    }
    return { type: 'midi' }
  }

  // Ref-based route beats stale React state (requestedPlayState / playbackRouteMode).
  if (mediaController.isMediaPlaybackRoute && mediaController.isMediaPlaybackRoute() && hasLinks) {
    return { type: 'media', linkNum: resolveActiveMediaLinkNum() }
  }
  if (mediaController.isMidiPlaybackRoute && mediaController.isMidiPlaybackRoute() && hasMusic) {
    return { type: 'midi' }
  }

  if (mediaController.requestedPlayState === 'playMedia' && hasLinks) {
    return { type: 'media', linkNum: resolveActiveMediaLinkNum() }
  }
  if (mediaController.requestedPlayState === 'playMidi' && hasMusic) {
    return { type: 'midi' }
  }

  const midiPreference = h.midiPreference || null
  const skipMidi = midiPreference === 'skip'

  // Queue item only when the active route has not already chosen an engine.
  if (isQueueActiveFn(queue) && currentItem
    && playingTune && currentItem.tuneId === playingTune.id) {
    return resolvePlaybackForItemFn(playingTune, currentItem, tunebook, {
      midiPreference: midiPreference || undefined,
    })
  }

  if (hasMusic && hasLinks && hasFilteredPlaybackVoices(playingTune)
    && mediaController.requestedPlayState !== 'playMedia'
    && !(mediaController.isMediaPlaybackRoute && mediaController.isMediaPlaybackRoute())) {
    return { type: 'midi' }
  }

  if (mediaController.playbackRouteMode === 'media' && hasLinks) {
    const linkNum = mediaController.mediaLinkNumber != null ? mediaController.mediaLinkNumber : 0
    return { type: 'media', linkNum: linkNum }
  }
  if (mediaController.playbackRouteMode === 'midi' && hasMusic) {
    return { type: 'midi' }
  }

  if (mediaController.hasActivePlaybackIntent && mediaController.hasActivePlaybackIntent()) {
    if (mediaController.isMidiPlaybackRoute && mediaController.isMidiPlaybackRoute() && hasMusic) {
      return { type: 'midi' }
    }
    if (mediaController.requestedPlayState === 'playMidi' && hasMusic) {
      return { type: 'midi' }
    }
    if (hasLinks) {
      const linkNum = mediaController.mediaLinkNumber != null ? mediaController.mediaLinkNumber : 0
      return { type: 'media', linkNum: linkNum }
    }
    if (hasMusic) {
      return { type: 'midi' }
    }
  }

  if (h.nowPlayingExpanded) {
    const midiFirst = !midiPreference || midiPreference === 'prefer'
    if (midiFirst && hasMusic) {
      return { type: 'midi' }
    }
    if (hasLinks) {
      const linkNum = mediaController.mediaLinkNumber != null ? mediaController.mediaLinkNumber : 0
      return { type: 'media', linkNum: linkNum }
    }
    if (hasMusic && !skipMidi) {
      return { type: 'midi' }
    }
  }

  return null
}

/**
 * True when the host should avoid forcing a MIDI route (user chose media).
 * options.forceMidiFromUrl: /playMidi deep link must replace a leftover media route.
 */
export function shouldSkipHostMidiRouteApply(mediaController, options) {
  if (!mediaController) return true
  if (mediaController.notationMidiOwner) return true
  if (typeof mediaController.isAndroidNativePlaybackStarting === 'function'
      && mediaController.isAndroidNativePlaybackStarting()) return true
  // Committed MIDI route wins over lagged React requestedPlayState (YouTube→MIDI).
  if (mediaController.isMidiPlaybackRoute && mediaController.isMidiPlaybackRoute()) {
    return false
  }
  if (options && options.forceMidiFromUrl) return false
  if (mediaController.requestedPlayState === 'playMedia') return true
  if (mediaController.isMediaPlaybackRoute && mediaController.isMediaPlaybackRoute()) return true
  return false
}
